import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Card,
  Table, TableHead, TableBody, TableRow, TableCell,
  Chip, IconButton, Alert, CircularProgress,
} from '@mui/material';
import { Add, CheckCircle, PauseCircle, Edit, Delete, Refresh } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import millingApi from '../../../services/millingApi';

const statusColors: Record<string, any> = { draft: 'default', active: 'success', inactive: 'warning' };

const GristRecipeList: React.FC = () => {
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      const res = await millingApi.getGristRecipes();
      setRecipes(res.data || res || []);
      setError(null);
    } catch (e: any) {
      setError(e.message || 'Failed to load grist recipes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleActivate = async (id: string) => { await millingApi.activateGristRecipe(id); load(); };
  const handleDeactivate = async (id: string) => { await millingApi.deactivateGristRecipe(id); load(); };
  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this grist recipe?')) return;
    await millingApi.deleteGristRecipe(id);
    load();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Grist / Blend Recipes</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={load}><Refresh /></IconButton>
          <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/milling/grist-recipes/new')}>
            New Recipe
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : (
        <Card>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Blend</TableCell>
                <TableCell>Target Protein</TableCell>
                <TableCell>Target Moisture</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recipes.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center">No grist recipes found</TableCell></TableRow>
              ) : recipes.map((r: any) => (
                <TableRow key={r.id} hover>
                  <TableCell><Typography fontWeight="bold" variant="body2">{r.recipeCode}</Typography></TableCell>
                  <TableCell>
                    {r.recipeName}
                    {r.description && <Typography variant="caption" display="block" color="textSecondary">{r.description}</Typography>}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {(r.lines || []).map((l: any, i: number) => (
                        <Chip
                          key={i}
                          label={`${l.grainVariety || l.grainType} ${l.weightPercent}%`}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {r.targetProteinMin != null && r.targetProteinMax != null
                      ? `${r.targetProteinMin}–${r.targetProteinMax}%`
                      : r.targetProteinMin != null ? `≥${r.targetProteinMin}%`
                      : '—'}
                  </TableCell>
                  <TableCell>{r.targetMoistureMax != null ? `≤${r.targetMoistureMax}%` : '—'}</TableCell>
                  <TableCell>
                    <Chip label={r.status} color={statusColors[r.status] || 'default'} size="small" />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton size="small" onClick={() => navigate(`/milling/grist-recipes/${r.id}/edit`)}><Edit fontSize="small" /></IconButton>
                      {r.status !== 'active' && (
                        <IconButton size="small" color="success" onClick={() => handleActivate(r.id)} title="Activate">
                          <CheckCircle fontSize="small" />
                        </IconButton>
                      )}
                      {r.status === 'active' && (
                        <IconButton size="small" color="warning" onClick={() => handleDeactivate(r.id)} title="Deactivate">
                          <PauseCircle fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton size="small" color="error" onClick={() => handleDelete(r.id)}><Delete fontSize="small" /></IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </Box>
  );
};

export default GristRecipeList;
